import { assertNever } from "@impero-com/common/utils/obj";
import {
    definePendoIds,
    TST_BLESSED_ID_COLUMN_SELECTION,
} from "@impero-com/ui";
import {
    CONTROL_ACTIVITIES_DATA_PARAM,
    CONTROL_ACTIVITIES_LIST_COLUMN_IDS,
    PersistedControlActivitiesDateFilter,
    PresetSearchParamsCustomDefaultValues,
} from "@impero-com/ui/domain";

export const CATEGORY_COLUMN_PREFIX = "category_";

export type ControlActivitiesScope =
    | { type: "all" }
    | { type: "controlProgram"; controlProgramId: number }
    | { type: "control"; controlId: string };

export const ALL_ACTIVITIES_SCOPE: ControlActivitiesScope = { type: "all" };

export const CONTROL_ACTIVITIES_LIST_LOCAL_STORAGE_KEY =
    "controlActivitiesList";

export function buildControlActivitiesListTableStateStorageKey(
    scope: ControlActivitiesScope
) {
    switch (scope.type) {
        case "all":
            return CONTROL_ACTIVITIES_LIST_LOCAL_STORAGE_KEY;
        case "controlProgram":
            return `${CONTROL_ACTIVITIES_LIST_LOCAL_STORAGE_KEY}ForProgram-${scope.controlProgramId}` as const;
        case "control":
            return `${CONTROL_ACTIVITIES_LIST_LOCAL_STORAGE_KEY}ForControl-${scope.controlId}` as const;
        default:
            return assertNever(scope);
    }
}

/**
 * Pendo selectors for this list. These are a contract with Pendo: renaming one
 * silently breaks the Feature tagged against it.
 */
export const CONTROL_ACTIVITIES_PENDO = definePendoIds({
    healthHeader: "control-activities/health-header",
    healthCell: "control-activities/health-cell",
    healthFilter: "control-activities/health-filter",
    timelineOpen: "control-activities/timeline-open",
    timelineModal: "control-activities/timeline-modal",
    timelineOpenActivityPage: "control-activities/timeline-open-activity-page",
});

export const CONTROL_ACTIVITIES_LIST_DATA_PARAMS_DEFAULTS = {
    [CONTROL_ACTIVITIES_DATA_PARAM.ACTIVE]: true,
    [CONTROL_ACTIVITIES_DATA_PARAM.ARCHIVED]: false,
    [CONTROL_ACTIVITIES_DATA_PARAM.DATE_FILTER]: null,
    [CONTROL_ACTIVITIES_DATA_PARAM.CLASSIFICATION_ID]: null,
};

/**
 * Filters on due date but bounds neither end, so no `due_date_start` /
 * `due_date_end` reaches the backend and every activity is in scope.
 */
export const UNBOUNDED_DUE_DATE_FILTER: PersistedControlActivitiesDateFilter = {
    mode: "dueDate",
    range: [null, null],
};

/**
 * Null means the default due-date window (one month back and one month forward).
 * Unbounded due-date filter means we dont pass the due date param and instead the
 * backend filters on the scope's id.
 */
export function buildControlActivitiesDefaultDateFilter(
    scope: ControlActivitiesScope
): PersistedControlActivitiesDateFilter {
    switch (scope.type) {
        case "all":
            return null;
        case "controlProgram":
        case "control":
            return UNBOUNDED_DUE_DATE_FILTER;
        default:
            return assertNever(scope);
    }
}

export function buildControlActivitiesListDataParamsDefaults(
    scope: ControlActivitiesScope
): Record<string, unknown> {
    return {
        ...CONTROL_ACTIVITIES_LIST_DATA_PARAMS_DEFAULTS,
        [CONTROL_ACTIVITIES_DATA_PARAM.DATE_FILTER]:
            buildControlActivitiesDefaultDateFilter(scope),
    };
}

export const CONTROL_ACTIVITIES_LIST_DEFAULT_STATE: PresetSearchParamsCustomDefaultValues =
    {
        columnVisibility: {
            [CONTROL_ACTIVITIES_LIST_COLUMN_IDS.FRICTION_TYPE]: false,
            [CONTROL_ACTIVITIES_LIST_COLUMN_IDS.DESCRIPTION]: false,
            [CONTROL_ACTIVITIES_LIST_COLUMN_IDS.CREATED]: false,
            [CONTROL_ACTIVITIES_LIST_COLUMN_IDS.FIRST_REVIEWER_DUE_DATE]: false,
            [CONTROL_ACTIVITIES_LIST_COLUMN_IDS.SECOND_REVIEWER_DUE_DATE]: false,
            [CONTROL_ACTIVITIES_LIST_COLUMN_IDS.COMPLETION_DUE_DATE]: false,
            [CONTROL_ACTIVITIES_LIST_COLUMN_IDS.FIRST_REVIEWED_DATE]: false,
            [CONTROL_ACTIVITIES_LIST_COLUMN_IDS.SECOND_REVIEWED_DATE]: false,
            [CONTROL_ACTIVITIES_LIST_COLUMN_IDS.COMPLETED_AND_REVIEWED_DATE]: false,
            [CONTROL_ACTIVITIES_LIST_COLUMN_IDS.CLAIMANT]: false,
            [CONTROL_ACTIVITIES_LIST_COLUMN_IDS.PERFORMER]: false,
            [CONTROL_ACTIVITIES_LIST_COLUMN_IDS.REVIEWED_DATE]: false,
            [CONTROL_ACTIVITIES_LIST_COLUMN_IDS.REVIEW_PERFORMER]: false,
            [CONTROL_ACTIVITIES_LIST_COLUMN_IDS.SECOND_REVIEWER]: false,
            [CONTROL_ACTIVITIES_LIST_COLUMN_IDS.SECOND_REVIEW_PERFORMER]: false,
            [CONTROL_ACTIVITIES_LIST_COLUMN_IDS.RESPONSIBLE_OR_REVIEWER]: false,
            // CLASSIFICATION_CLASS is deliberately absent: the column only
            // exists when a classification was requested
            [CONTROL_ACTIVITIES_LIST_COLUMN_IDS.ARCHIVED]: false,
            [CONTROL_ACTIVITIES_LIST_COLUMN_IDS.CONTROL_PROGRAM_STATUS]: false,
        },
        sorting: [
            {
                id: CONTROL_ACTIVITIES_LIST_COLUMN_IDS.DUE_DATE,
                desc: false,
            },
        ],
        columnPinning: {
            left: [TST_BLESSED_ID_COLUMN_SELECTION],
        },
        columnOrder: [
            TST_BLESSED_ID_COLUMN_SELECTION,
            CONTROL_ACTIVITIES_LIST_COLUMN_IDS.CONTROL_PROGRAM_ID,
            CONTROL_ACTIVITIES_LIST_COLUMN_IDS.CONTROL_PROGRAM_STATUS,
            CONTROL_ACTIVITIES_LIST_COLUMN_IDS.CONTROL_ID,
            CONTROL_ACTIVITIES_LIST_COLUMN_IDS.ENTITY,
            CONTROL_ACTIVITIES_LIST_COLUMN_IDS.DESCRIPTION,
            CONTROL_ACTIVITIES_LIST_COLUMN_IDS.PERIOD,
            CONTROL_ACTIVITIES_LIST_COLUMN_IDS.TAGS,
            CONTROL_ACTIVITIES_LIST_COLUMN_IDS.CREATED,
            CONTROL_ACTIVITIES_LIST_COLUMN_IDS.COMPLETION_DUE_DATE,
            CONTROL_ACTIVITIES_LIST_COLUMN_IDS.FIRST_REVIEWER_DUE_DATE,
            CONTROL_ACTIVITIES_LIST_COLUMN_IDS.SECOND_REVIEWER_DUE_DATE,
            CONTROL_ACTIVITIES_LIST_COLUMN_IDS.DUE_DATE,
            CONTROL_ACTIVITIES_LIST_COLUMN_IDS.TIME_TO_DUE_DATE,
            CONTROL_ACTIVITIES_LIST_COLUMN_IDS.COMPLETED_DATE,
            CONTROL_ACTIVITIES_LIST_COLUMN_IDS.FIRST_REVIEWED_DATE,
            CONTROL_ACTIVITIES_LIST_COLUMN_IDS.SECOND_REVIEWED_DATE,
            CONTROL_ACTIVITIES_LIST_COLUMN_IDS.REVIEWED_DATE,
            CONTROL_ACTIVITIES_LIST_COLUMN_IDS.COMPLETED_AND_REVIEWED_DATE,
            CONTROL_ACTIVITIES_LIST_COLUMN_IDS.RESPONSIBLE,
            CONTROL_ACTIVITIES_LIST_COLUMN_IDS.CLAIMANT,
            CONTROL_ACTIVITIES_LIST_COLUMN_IDS.PERFORMER,
            CONTROL_ACTIVITIES_LIST_COLUMN_IDS.REVIEWER,
            CONTROL_ACTIVITIES_LIST_COLUMN_IDS.REVIEW_PERFORMER,
            CONTROL_ACTIVITIES_LIST_COLUMN_IDS.SECOND_REVIEWER,
            CONTROL_ACTIVITIES_LIST_COLUMN_IDS.SECOND_REVIEW_PERFORMER,
            CONTROL_ACTIVITIES_LIST_COLUMN_IDS.RESPONSIBLE_OR_REVIEWER,
            CONTROL_ACTIVITIES_LIST_COLUMN_IDS.WORKFLOW_STATUS,
            CONTROL_ACTIVITIES_LIST_COLUMN_IDS.HEALTH,
            CONTROL_ACTIVITIES_LIST_COLUMN_IDS.CLASSIFICATION_CLASS,
            CONTROL_ACTIVITIES_LIST_COLUMN_IDS.ARCHIVED,
        ],
        dataParams: CONTROL_ACTIVITIES_LIST_DATA_PARAMS_DEFAULTS,
    };

export function buildControlActivitiesListDefaultState(
    categoryNames: string[],
    scope: ControlActivitiesScope
): PresetSearchParamsCustomDefaultValues {
    const dynamicVisibility: Record<string, boolean> = {};
    const dynamicColumnOrder: string[] = [];
    for (const categoryName of categoryNames) {
        const columnId = `${CATEGORY_COLUMN_PREFIX}${categoryName}`;
        dynamicVisibility[columnId] = false;
        dynamicColumnOrder.push(columnId);
    }
    return {
        ...CONTROL_ACTIVITIES_LIST_DEFAULT_STATE,
        columnVisibility: {
            ...CONTROL_ACTIVITIES_LIST_DEFAULT_STATE.columnVisibility,
            ...dynamicVisibility,
        },
        columnOrder: [
            ...(CONTROL_ACTIVITIES_LIST_DEFAULT_STATE.columnOrder ?? []),
            ...dynamicColumnOrder,
        ],
        dataParams: buildControlActivitiesListDataParamsDefaults(scope),
    };
}
